import { AfterViewChecked, Component, OnDestroy, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { ApiService } from '../../services/api.service';
import { Globals } from '../../services/globals.service';
import { UserService } from '../../services/user.service';
import { AnimationItem } from 'lottie-web';
import { constants, validVoucherPrefixes } from '../../shared/constants/constants';
import { RegistrationService } from '../../services/registration.service';
import * as moment from 'moment';
import { onboardingSteps } from '../../shared/constants/onboarding.constants';
import { macFormat } from '../../shared/constants/misc.constants';
import { MeterService } from '../../services/meter.service';
import { TrackAnalyticsService, TRACKING_EVENT } from '../../services/track-analytics.service';
import { Popover } from '../../popovers/popover/popover.service';
import { PopoverConfigService } from '../../popovers/static.popover.config';
import { OpticalReaderService } from '../../services/optical-reader.service';
import { of, retryWhen, Subject, takeUntil, throwError, timer } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap, concatMap } from 'rxjs/operators';
import { FirmwareUpdateService } from '../../services/firmware-update.service';
import { MeterStatuses } from '../../shared/enums/meter-statuses.enum';
import { TextMaskConfig } from 'angular2-text-mask';
import { StorageAttributes } from '../../shared/constants/storage-attributes.constants';
import { LocalOptInService } from '../../services/local-opt-in.service';
import {
    FormControl,
    FormGroup,
    UntypedFormControl,
    UntypedFormGroup,
    Validators,
} from '@angular/forms';
import zxcvbnCommonPackage from '@zxcvbn-ts/language-common';
import { zxcvbn, zxcvbnOptions } from '@zxcvbn-ts/core';
import { AnimationOptions } from 'ngx-lottie';
import { OptInService } from '../../services/opt-in.service';
import { ErrorDialogComponent } from '../../components/error-dialog/error-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { SupportModalComponent } from '../../components/support-modal/support-modal.component';
import { TranslateService } from '@ngx-translate/core';
import { VideoDialogComponent } from '../../components/video-dialog/video-dialog.component';

@Component({
    selector: 'app-register-component',
    templateUrl: './register.component.html',
    styleUrls: [
        'register-base.component.scss',
        'register.component.scss',
        'register-connection.component.scss',
        'register-sections.component.scss',
    ],
    viewProviders: [ApiService],
    providers: [Globals],
})
export class RegisterComponent implements OnInit, OnDestroy, AfterViewChecked {
    constructor(
        public apiService: ApiService,
        private title: Title,
        private router: Router,
        private route: ActivatedRoute,
        private toast: ToastrService,
        private globals: Globals,
        private userService: UserService,
        private registration: RegistrationService,
        private meter: MeterService,
        private tracking: TrackAnalyticsService,
        private popover: Popover,
        private opticalReader: OpticalReaderService,
        private updateService: FirmwareUpdateService,
        private localOptInService: LocalOptInService,
        private optInService: OptInService,
        public dialog: MatDialog,
        private translate: TranslateService,
        private popoverConfigService: PopoverConfigService
    ) {}

    private readonly plugMacValidationRegex = /^[0-9A-Fa-f]{12}$/g;
    private readonly opticalReaderMacValidationRegex =
        /^([0-9A-Fa-f]{2}[:-]?){7}([0-9A-Fa-f]{2})$/g;
    private readonly registrationOnboardRetryDuration = 300000;
    private readonly registrationOnboardRetryInterval = 5000;
    private readonly firmwareUpdateSearchDuration = 600000;
    private readonly firmwareUpdateSearchInterval = 5000;
    private readonly firmwareUpdateMaxDuration: 7200000;
    private readonly allowedChars =
        '^ $ * . [ ] { } ( ) ? „ ! @ # % & / \\ , > < ‚ : ; | _ ~ = + - `';
    private readonly invalidChars = 'ü, ö, ä, Ü, Ö, Ä, ß';

    private popoverOpen = false;
    private preventPinInfoPopover = false;
    private meterPin = null;

    private previousStatus = '';
    private registrationTries = 0;
    private registrationSub = null;
    private lastRegistrationCall = new Date().getTime();

    private passwordRegex = /^(?=.*?[A-Z])(?!.*[ßüöäÜÖÄ])(?=.*?[0-9]).{8,}$/;
    private zxcvbnLocalOptions = {
        graphs: zxcvbnCommonPackage.adjacencyGraphs,
        dictionary: {
            ...zxcvbnCommonPackage.dictionary,
        },
    };

    readonly onboardingSteps = onboardingSteps;
    readonly moment = moment;
    readonly macAddressMask: TextMaskConfig = {
        mask: [
            /[0-9A-Fa-f]/,
            /[0-9A-Fa-f]/,
            /[0-9A-Fa-f]/,
            /[0-9A-Fa-f]/,
            ':',
            /[0-9A-Fa-f]/,
            /[0-9A-Fa-f]/,
            /[0-9A-Fa-f]/,
            /[0-9A-Fa-f]/,
            ':',
            /[0-9A-Fa-f]/,
            /[0-9A-Fa-f]/,
            /[0-9A-Fa-f]/,
            /[0-9A-Fa-f]/,
        ],
    };
    readonly macPlaceholder: string = '15E1:8000:89F1';
    readonly opticalReaderMacAddressMask: TextMaskConfig = {
        mask: [
            /[0-9A-Fa-f]/,
            /[0-9A-Fa-f]/,
            /[0-9A-Fa-f]/,
            /[0-9A-Fa-f]/,
            ':',
            /[0-9A-Fa-f]/,
            /[0-9A-Fa-f]/,
            /[0-9A-Fa-f]/,
            /[0-9A-Fa-f]/,
            ':',
            /[0-9A-Fa-f]/,
            /[0-9A-Fa-f]/,
            /[0-9A-Fa-f]/,
            /[0-9A-Fa-f]/,
            ':',
            /[0-9A-Fa-f]/,
            /[0-9A-Fa-f]/,
            /[0-9A-Fa-f]/,
            /[0-9A-Fa-f]/,
        ],
    };
    readonly opticalReaderMacPlaceholder: string = '15E1:8000:89F1:0805';

    passwordVisible1 = false;
    passwordVisible2 = false;
    refresh: any = [];
    checkMeterStatusInterval: any = [];

    currentStep = this.onboardingSteps.introVideos;
    meterStatus = 0;

    device = 'box';
    deviceTitle: string;

    isBox2 = false;

    // structure the amount of failed connections by status
    statusTries = { step0: 0, step1: 0, step2: 0 };
    // status error to be displayed
    statusError = { title: '', text: '' };
    connect = { type: null, tries: 0 };
    // registered user
    user = { voucher: null, email: null, password: null, mac: null };
    format = macFormat;
    input: any = { mac: '' };
    private noDataPopoverShown = false;

    // animation
    lottieConfig2: AnimationOptions = {
        path: 'assets/anim/onboarding_connection.json',
        autoplay: false,
        name: 'Smart Meter Animation',
        loop: true,
        // renderer: 'canvas'
    };
    loadingLottieConfig = {
        path: 'assets/anim/onboarding-loading.json',
        autoplay: true,
        name: 'Loading Animation',
        loop: true,
        renderer: 'svg',
    };
    animationReady = false;
    anim: AnimationItem;

    isEDGUser = false;
    readerPIN: number = null;
    pinEntrySubmissionDisabled = true;

    plugMac = null;
    deviceMac = null;
    opticalReaderMac = null;

    firstMac = null;
    secondMac = null;

    registrationOnboardSuccessful = false;
    firmwareUpdateStatus: 'loading' | 'uptodate' | 'error' = 'loading';
    commissionStatus: 'initial' | 'loading' | 'success' | 'error' = 'initial';
    firmwareIsUpdating = false;
    firmwareUpdateFinishedOrFailed = false;
    lastMeterStatus = null;

    navigationFromDashboard = false;

    passwordForm = new UntypedFormGroup({
        password1: new UntypedFormControl('', [Validators.required]),
        password2: new UntypedFormControl('', [Validators.required]),
    });
    setPasswordDisabled = true;
    currentPasswordScore = 0;
    voucherForm = new UntypedFormGroup({
        email: new UntypedFormControl('', [Validators.required]),
        code: new UntypedFormControl('', [Validators.required]),
    });
    deviceMacForm = new FormGroup({
        mac: new FormControl('', [Validators.required, Validators.minLength(12)]),
    });
    readerMacForm = new FormGroup({
        mac: new FormControl('', [Validators.required]),
    });

    getDeviceTitle(): string {
        const box2EonTitle = this.translate.instant('common.device.box2Eon');
        const plugEonTitle = this.translate.instant('common.device.plugEon');

        return this.isBox2 ? box2EonTitle : plugEonTitle;
    }

    isSmartBox2() {
        return this.isBox2;
    }

    ngOnInit() {
        localStorage.setItem('userOnboardingInProgress', 'in_progress');
        this.translate
            .get('screens.register.pageTitle.register')
            .subscribe((translatedTitle: string) => {
                this.title.setTitle(translatedTitle);
            });

        // get previously stored device
        const stored_device = this.userService.getUserDevice();
        if (stored_device !== null && stored_device !== undefined) {
            if (stored_device === constants.application.devices.box) {
                this.device = 'box';
                this.deviceTitle = this.translate.instant('common.device.boxEon');
            } else if (stored_device === constants.application.devices.plug) {
                this.device = 'plug';
                this.deviceTitle = this.translate.instant('common.device.plugEon');
            } else if (stored_device === constants.application.devices.plug_optical) {
                this.device = 'plug';
                this.deviceTitle = this.translate.instant('common.device.plugEon');
                this.isEDGUser = true;
            } else if (
                stored_device === constants.application.devices.smart_box ||
                stored_device === constants.application.devices.smart_box2
            ) {
                this.device = 'plug';
                this.deviceTitle = this.translate.instant('common.device.box2Eon');
                this.isEDGUser = true;
            }
        }

        this.initializeRouteParamterHandling();

        if (this.globals.getIonaMacId() !== null && this.globals.getIonaMacId().length > 0) {
            this.input.mac = this.globals.getIonaMacId();
        }

        zxcvbnOptions.setOptions(this.zxcvbnLocalOptions);
        this.setupFormHandling();
    }

    ngOnDestroy() {
        clearInterval(this.refresh);
        clearInterval(this.checkMeterStatusInterval);
    }

    ngAfterViewChecked() {}

    resetMacAddressEntry(): void {
        this.deviceMacForm.setValue({ mac: '' });
        this.plugMacValidationRegex.lastIndex = 0;
        this.readerMacForm.setValue({ mac: '' });
        this.opticalReaderMacValidationRegex.lastIndex = 0;
        this.setStep(onboardingSteps.devices.plug.macEntry);
        this.commissionStatus = 'initial';
    }

    openSupportDialog(): void {
        const dialogRef = this.dialog.open(SupportModalComponent, {
            width: '400px',
            data: {
                text: this.translate.instant('screens.register.onBoarding.supportDialogText'),
                showCloseButton: false,
            },
        });

        dialogRef.afterClosed().subscribe(() => {
            console.log('The other modal was closed');
        });
    }

    openErrorDialog(): void {
        const dialogRef = this.dialog.open(ErrorDialogComponent, {
            width: '400px',
            data: {
                title: this.translate.instant('common.errors.connectionFailed'),
                message: this.translate.instant('common.errors.connectionFailed'),
                showButton1: true,
                button1Text: this.translate.instant('common.errors.retryButton'),
                showButton2: true,
                button2Text: this.translate.instant('common.supportButton'),
                showButton3: true,
                button3Text: this.translate.instant('common.faqButton'),
            },
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result === 'button1') {
                this.resetMacAddressEntry();
            } else if (result === 'button2') {
                this.openSupportDialog();
            } else if (result === 'button3') {
                this.openFAQ();
            }
        });
    }

    openFAQ(): void {
        window.open('https://app-content.n2g-iona.net/help/esc/home?showNav=true', '_blank');
    }

    hardwareNotReady(): void {
        this.apiService.logoutUser();
        this.router.navigate(['login']);
    }

    onPinChange(pin): void {
        if (pin.length >= 1) {
            this.readerPIN = parseInt(pin, 10);
            this.pinEntrySubmissionDisabled = false;
        } else {
            this.pinEntrySubmissionDisabled = true;
        }
    }

    determinePasswordScoreRatingText(): string {
        if (this.currentPasswordScore <= 2) {
            return this.translate.instant('common.unsecure');
        } else if (this.currentPasswordScore === 3) {
            return this.translate.instant('common.secure');
        }
        return this.translate.instant('common.verySafe');
    }

    openPasswordInfoModal() {
        this.translate
            .get([
                'screens.register.passwordPolicy.title',
                'screens.register.passwordPolicy.lengthTitle',
                'screens.register.passwordPolicy.length',
                'screens.register.passwordPolicy.upperLowerTitle',
                'screens.register.passwordPolicy.numberTitle',
                'screens.register.passwordPolicy.invalidCharsTitle',
                'screens.register.passwordPolicy.allowedCharsTitle',
            ])
            .subscribe((translations) => {
                const translatedText = `
            <p><strong>${translations['screens.register.passwordPolicy.lengthTitle']}</strong><br>${translations['screens.register.passwordPolicy.length']}</p>
            <p>${translations['screens.register.passwordPolicy.upperLowerTitle']}</p>
            <p>${translations['screens.register.passwordPolicy.numberTitle']}</p>
            <p><strong>${translations['screens.register.passwordPolicy.invalidCharsTitle']}</strong><br>${this.invalidChars}</p>
            <p><strong>${translations['screens.register.passwordPolicy.allowedCharsTitle']}</strong><br>${this.allowedChars}</p>
        `;

                this.dialog.open(SupportModalComponent, {
                    width: '450px',
                    data: {
                        title: translations['screens.register.passwordPolicy.title'],
                        text: translatedText,
                        isHtmlText: true,
                        showCloseButton: true,
                        alignLeft: true,
                        morePadding: true,
                        fontSize: '24px',
                        titleFontSize: '32px',
                    },
                });
            });
    }

    determinePasswordStrengthIndicatorColor(index): string {
        if (index <= this.currentPasswordScore) {
            return this.determinePasswordStrengthColor();
        }
        return 'transparent';
    }

    determinePasswordStrengthColor(): string {
        const colors = ['#b80000', '#ea1b0a', '#f05548', '#e3e000', '#1ea2b1'];
        return colors[this.currentPasswordScore];
    }

    /**
     * Voucher validieren
     */
    validateVoucher() {
        const values = this.voucherForm.value;
        const voucher = values.code;
        const email = values.email;

        const validities = validVoucherPrefixes.map((el: string) =>
            voucher.startsWith(el.toUpperCase())
        );
        if (validities.filter((el) => el === true).length === 0) {
            this.toast.error(this.translate.instant('common.errors.voucherCodeInvalid'), 'Fehler');
            return;
        }
        this.apiService.validateVoucher(voucher.toUpperCase(), email).subscribe(
            // this.registration.validateVoucher(voucher, email).subscribe(
            () => {
                // Erfolg -> Email und Voucher im User setzen und weiter gehts zum nächsten Step
                this.user.voucher = voucher;
                this.user.email = email;
                this.setStep(this.onboardingSteps.passwordEntry);
                this.tracking.onboarding_voucher(true);
            },
            (error_res: any) => {
                if (error_res.error) {
                    switch (error_res.error.code) {
                        case 106: {
                            this.toast.error(
                                this.translate.instant('common.errors.voucherUnknown')
                            );
                            break;
                        }
                        default: {
                            this.toast.error(
                                this.translate.instant('common.errors.voucherValidationFailed')
                            );
                        }
                    }
                    this.tracking.onboarding_voucher(false);
                } else {
                    this.toast.error(this.translate.instant('common.errors.voucherMissing'));
                }
            }
        );
    }

    /**
     * Nutzer registrieren
     */
    registerUser() {
        const values = this.passwordForm.value;
        if (values.password1 !== values.password2) {
            this.toast.error(this.translate.instant('common.errors.passwordsDoNotMatch'));
            return;
        }

        const user = {
            email_address: this.user.email,
            pincode: values.password1,
            voucher_code: this.user.voucher.toUpperCase(),
        };

        this.apiService.registerUser(user).subscribe(
            () => {
                this.user.password = values.password1;
                this.apiService.loginUser(this.user.email, this.user.password, true);

                this.apiService.onLoggedIn.subscribe(() => {
                    this.apiService.getRegistrationModel().subscribe(
                        (model_response: any) => {
                            if (model_response.status === 'ok') {
                                if ('model_identifier' in model_response.data) {
                                    switch (model_response.data.model_identifier) {
                                        case constants.application.devices.plug:
                                            this.selectDevice('plug');
                                            break;
                                        case constants.application.devices.plug_optical:
                                            this.selectDevice('plug', true);
                                            break;
                                        case constants.application.devices.smart_box:
                                            this.selectDevice(
                                                'plug',
                                                false,
                                                true,
                                                constants.application.devices.smart_box
                                            );
                                            break;
                                        case constants.application.devices.smart_box2:
                                            this.selectDevice(
                                                'plug',
                                                false,
                                                true,
                                                constants.application.devices.smart_box2
                                            );
                                            break;
                                        default:
                                            this.selectDevice('box');
                                    }
                                }
                            }
                        },
                        (error) => {
                            console.log('Error:', error);
                        }
                    );
                });
            },
            (err: any) => {
                if ('error' in err) {
                    if ('error' in err.error) {
                        if (err.error.error.code === 264) {
                            const msg = err.error.error.message;
                            if (msg.includes('MAX length')) {
                                this.toast.error(
                                    this.translate.instant('common.errors.passwordTooLong'),
                                    this.translate.instant('common.errors.invalidPassword'),
                                    { timeOut: 6000 }
                                );
                            } else if (msg.includes('Special characters')) {
                                this.toast.error(
                                    this.translate.instant('common.errors.invalidPasswordChars'),
                                    this.translate.instant('common.errors.invalidPassword'),
                                    { timeOut: 6000 }
                                );
                            }
                        }
                        if (err.error.error.code === 102) {
                            const msg = err.error.error.message;
                            if (msg.includes('already active on')) {
                                this.toast.error(
                                    this.translate.instant('common.errors.accountAlreadyCreated'),
                                    this.translate.instant('common.errors.error'),
                                    { timeOut: 6000 }
                                );
                            }
                        }
                    }
                }
                if (err._body) {
                    const error: any = JSON.parse(err._body);
                    switch (error.error.code) {
                        case 102: {
                            this.toast.info(
                                this.translate.instant('common.info.alreadyRegistered')
                            );
                            this.setStep(this.onboardingSteps.devices.box.powerConnect);
                            break;
                        }
                        case 264: {
                            console.log('CASE 264 ');
                            break;
                        }
                        default: {
                            const errorMessage = this.translate.instant(
                                'common.errors.registrationFailed'
                            );
                            this.toast.error(errorMessage);
                        }
                    }
                } else {
                    const errorMessage = this.translate.instant('common.errors.registrationFailed');
                    this.toast.error(errorMessage);
                }
            }
        );
    }

    /**
     * Meter registrieren
     * @param mac
     * @param doThrowError
     */
    registerDevice(mac: string, doThrowError: boolean = true) {
        let newMac = mac.replace(/:/g, '');
        const macPart1 = newMac.substring(0, 6);
        const macPart2 = newMac.substring(6);
        newMac = macPart1 + '0000' + macPart2;

        this.apiService.registerDevice(newMac).subscribe(
            () => {
                this.registerDeviceSuccess();
                this.selectConnectionStep(this.device);
            },
            (error: any) => {
                if (error.error.error.code === 102) {
                    if (this.device === 'plug') {
                        if (this.isEDGUser) {
                            this.setStep(this.onboardingSteps.devices.plug.connectingOR);
                        } else {
                            this.setStep(this.onboardingSteps.devices.plug.connecting);
                        }
                    } else {
                        this.setStep(this.onboardingSteps.devices.box.connecting);
                    }
                    this.registerDeviceSuccess();
                } else if (error.error.error.code === 110) {
                    const errorMessage = this.translate.instant('common.errors.registrationFailed');
                    this.toast.error(errorMessage);
                } else if (error.error.error.code === 999) {
                    const errorMessage = this.translate.instant('common.errors.registrationFailed');
                    this.toast.error(errorMessage);
                } else if ('_body' in error) {
                    try {
                        JSON.parse(error._body);
                    } catch (e) {
                        if (doThrowError) {
                            this.registerDeviceError();
                        }
                    }
                } else if (doThrowError) {
                    this.registerDeviceError();
                }
            }
        );
    }

    private enterMeterStatusUpdate() {
        this.checkMeterStatus();

        this.tracking.onboarding_status(TRACKING_EVENT.ONBOARDING_FINISH, {
            connection_type: this.connect.type,
            user_mac: this.user.mac,
        });

        this.apiService.optInDevice().subscribe(() => {});

        this.setStep(this.onboardingSteps.devices.plug.connecting);
    }

    private connectMeterWithPin(): void {
        this.meter.putOpticalReaderPin(this.readerPIN).subscribe(() => {});
    }

    private registerDeviceSuccess() {
        this.checkMeterStatus();

        this.tracking.onboarding_status(TRACKING_EVENT.ONBOARDING_FINISH, {
            connection_type: this.connect.type,
            user_mac: this.user.mac,
        });

        this.apiService.optInDevice().subscribe(
            () => null,
            (e) => console.log('Opt-In failed with:', e.error.error.code, e.error.error.message)
        );
    }

    registerDeviceError() {
        this.connect.tries++;
        switch (this.connect.type) {
            case 'LAN': {
                this.input.mac = '';
                this.globals.resetIonaMacId();
                this.setStep(this.onboardingSteps.devices.box.notFoundError);
                break;
            }
            case 'AP': {
                if (this.connect.tries >= 3) {
                    this.setStep('Ü');
                } else {
                    this.setStep(this.onboardingSteps.devices.box.connectionError);
                }
                break;
            }
        }
    }

    handleAnimation(anim: any): void {
        this.anim = anim;
        this.anim.stop();
        this.playAnimationSegment(0);
        setTimeout(() => {
            this.playAnimationSegment(0);
            this.animationReady = true;
            this.checkMeterStatus(this.navigationFromDashboard);
        }, 100);
    }

    playAnimationSegment(step: number): void {
        if (this.anim === null || this.anim === undefined) {
            return;
        }
        if (!this.animationReady) {
            return;
        }
        if (step === 0) {
            setTimeout(() => {
                this.anim.playSegments([0, 85], true);
            }, 150);
        } else if (step === 1) {
            setTimeout(() => {
                this.anim.playSegments([205, 318], true);
            }, 150);
        } else if (step === 2) {
            setTimeout(() => {
                this.anim.playSegments([350, 508], true);
            }, 150);
        } else if (step === 3) {
            setTimeout(() => {
                this.anim.playSegments([657, 959], true);
            }, 150);
            // setTimeout(() => {
            //     this.anim.playSegments([655, 655], true);
            // }, 150);
        } else if (step === 4) {
            setTimeout(() => {
                this.anim.playSegments([657, 959], true);
            }, 150);
        }
    }

    /**
     * Regelmäßig den aktuellen Status der Meter-Registrierung abfragen
     */
    checkMeterStatus(alreadyStarted = false) {
        if (this.isEDGUser) {
            if (this.animationReady) {
                this.playAnimationSegment(0);
            }

            const sub = timer(0, 5000)
                .pipe(
                    mergeMap(() => {
                        if (!alreadyStarted) {
                            return this.meter.putOpticalReaderPin(this.readerPIN).pipe(
                                map(() => true),
                                catchError(() => of(false))
                            );
                        }
                        return of(true);
                    }),
                    map((res) => {
                        if (res) {
                            return true;
                        }
                    })
                )
                .subscribe((res) => {
                    if (res) {
                        sub.unsubscribe();
                        this.meterStatusCallERNA();
                        this.checkMeterStatusInterval = setInterval(() => {
                            this.meterStatusCallERNA();
                        }, 5000);
                    }
                });
            return;
        }

        this.meterStatusCall();
        this.checkMeterStatusInterval = setInterval(() => {
            this.meterStatusCall();
        }, 5000);
    }

    meterStatusCallERNA() {
        this.opticalReader.getOpticalReaderStatus(true).subscribe(
            (response: any) => {
                if (!('current_status' in response.electricity)) {
                    this.increaseMeterStatusErrorTries();
                    return;
                }
                const data = response.electricity;

                const status = data.current_status.toUpperCase();
                if (
                    this.previousStatus === MeterStatuses.UPDATE_INSTALLING &&
                    status !== MeterStatuses.UPDATE_INSTALLING
                ) {
                    this.updateService.onUpdateStateReceived.next(null);
                }
                this.previousStatus = status;

                this.meterPin = data.smartreader.pincode;
                const pinInputRequired =
                    data.smartreader.pin_entry_mode === 'manual_push_button' ||
                    data.smartreader.pin_entry_mode === 'manual_torch';
                const meterUnlocked = data.smartreader.meter_unlocked;

                if (!meterUnlocked && pinInputRequired) {
                    if (!this.preventPinInfoPopover) {
                        this.showManualPinEntryOverlay();
                    }
                }

                const skipToPinVerification = false;
                switch (status) {
                    case MeterStatuses.READY_FOR_METER_INCLUSION:
                        if (this.meterStatus !== 1) {
                            this.meterStatus = 1;
                            this.globals.resetIsMeterConnected();
                            this.playAnimationSegment(1);
                        }
                        break;
                    case MeterStatuses.CONNECTED_WITH_METER:
                    case 'COMMISSION_IN_PROGRESS':
                        if (this.meterStatus !== 2) {
                            if (data.smartreader.meter_unlocked) {
                                this.globals.setIsMeterConnected();
                                if (!skipToPinVerification) {
                                    clearInterval(this.checkMeterStatusInterval);
                                    this.setStep(onboardingSteps.accountSetup);
                                }
                            }
                            this.meterStatus = 2;
                            this.playAnimationSegment(2);
                            this.globals.resetIsMeterConnected();
                            this.showUpdateAvailableOverlay();
                        }
                        break;
                    case MeterStatuses.VERIFYING_PIN:
                        if (this.meterStatus !== 3) {
                            this.meterStatus = 3;
                            this.globals.setIsMeterConnected();
                            this.playAnimationSegment(3);
                        }

                        break;
                    case MeterStatuses.PIN_FAILED:
                        this.handlePinFailedState();
                        break;
                    case MeterStatuses.CONTACT_NO_DATA_FROM_METER:
                        this.openErrorPopover(data);
                        break;
                    case MeterStatuses.CONTACT_WRONG_METER_SERIAL:
                        this.openErrorPopover(data);
                        break;
                    case MeterStatuses.UPDATE_INSTALLING:
                        if ('firmware_update_progress' in data.smartreader) {
                            const progress = data.smartreader.firmware_update_progress;
                            this.updateService.onUpdateStateReceived.next(progress);
                            const firmwareUpdatePopoverConfig =
                                this.popoverConfigService.getFirmwareUpdatePopoverConfig();
                            this.popover
                                .open(firmwareUpdatePopoverConfig)
                                .afterClosed$.subscribe(() => {
                                    this.popoverOpen = false;
                                });
                            this.popoverOpen = true;
                        }
                        break;
                    case MeterStatuses.RADIO_LINK_LOST:
                        if (
                            !this.shouldTriggerTimeBasedOverlay(
                                StorageAttributes.RADIO_LINK_LOST_INFO,
                                'hours',
                                24
                            )
                        ) {
                            break;
                        }
                        if (this.popoverOpen) {
                            break;
                        }
                        const radioLinkLostPopoverConfig =
                            this.popoverConfigService.getRadioLinkLostPopoverConfig();
                        this.popover
                            .open(radioLinkLostPopoverConfig)
                            .afterClosed$.subscribe(() => {});
                        this.popoverOpen = true;
                        break;
                    default:
                        this.meterStatus = 0;
                        this.globals.resetIsMeterConnected();
                        if (this.animationReady) {
                            this.playAnimationSegment(0);
                        }
                }
                this.increaseMeterStatusErrorTries();
            },
            (error) => {
                console.log('Error:', error);
                this.increaseMeterStatusErrorTries();
            }
        );
    }

    meterStatusCall() {
        this.apiService.getDeviceStatus().subscribe(
            (response: any) => {
                if (!('data' in response)) {
                    this.increaseMeterStatusErrorTries();
                    return;
                }

                if (!('current_status' in response.data)) {
                    this.increaseMeterStatusErrorTries();
                    return;
                }

                switch (response.data.current_status.toUpperCase()) {
                    case MeterStatuses.READY_FOR_METER_INCLUSION:
                        if (this.animationReady) {
                            if (this.meterStatus !== 1) {
                                this.playAnimationSegment(1);
                                this.meterStatus = 1;
                                this.globals.resetIsMeterConnected();
                            }
                        }

                        break;
                    case MeterStatuses.CONTACT_WITH_METER:
                        if (this.animationReady) {
                            if (this.meterStatus !== 2) {
                                this.playAnimationSegment(2);
                                this.meterStatus = 2;
                                this.globals.resetIsMeterConnected();
                            }
                        }

                        break;
                    case MeterStatuses.CONNECTED_WITH_METER:
                        if (this.animationReady) {
                            if (this.meterStatus !== 3) {
                                this.playAnimationSegment(3);
                                this.meterStatus = 3;
                                this.globals.setIsMeterConnected();
                                clearInterval(this.checkMeterStatusInterval);
                            }
                        }

                        break;
                    default:
                        this.playAnimationSegment(0);
                }

                this.increaseMeterStatusErrorTries();
            },
            (error) => {
                console.log('Error:', error);
                this.increaseMeterStatusErrorTries();
            }
        );
    }

    increaseMeterStatusErrorTries() {
        // Versuche erhöhen
        switch (this.meterStatus) {
            case 0:
                this.statusTries.step0++;
                break;
            case 1:
                this.statusTries.step1++;
                break;
            case 2:
                this.statusTries.step2++;
                break;
        }

        // Prüfen ob Maximum erreicht
        switch (this.meterStatus) {
            case 0: // Step0 5 Minuten -> 60 Calls
                if (this.statusTries.step0 >= 60) {
                    this.statusError.title = this.translate.instant(
                        'common.errors.noRegistrationPossibleTitle'
                    );
                    this.statusError.text = this.translate.instant(
                        'common.errors.noRegistrationPossibleText'
                    );
                    this.statusTries = { step0: 0, step1: 0, step2: 0 };
                    this.setStep(this.onboardingSteps.detailFailed);
                    clearInterval(this.checkMeterStatusInterval);
                }
                break;
            case 1: // Step1 2 Minuten -> 24 Calls
                // if (this.statusTries.step1 >= 60) {
                if (this.statusTries.step1 >= 24) {
                    this.statusError.title = this.translate.instant(
                        'common.errors.smartMeterNotFound'
                    );
                    this.statusError.text = this.statusError.title = this.translate.instant(
                        'common.errors.smartMeterSearchFailure'
                    );
                    this.statusTries = { step0: 0, step1: 0, step2: 0 };
                    this.setStep(this.onboardingSteps.detailFailed);
                    clearInterval(this.checkMeterStatusInterval);
                }
                break;
            case 2: // Step2 10 Minuten -> 120 Calls
                if (this.statusTries.step2 >= 120) {
                    this.statusError.title = this.translate.instant(
                        'common.errors.noSmartMeterConnection'
                    );
                    this.statusError.text = this.translate.instant(
                        'common.errors.failedToConnectToSmartMeter'
                    );
                    this.statusTries = { step0: 0, step1: 0, step2: 0 };
                    this.setStep(this.onboardingSteps.detailFailed);
                    clearInterval(this.checkMeterStatusInterval);
                }
                break;
        }
    }

    /**
     * Zurücksetzen der Meter-Registrierung und von vorn beginnen
     */
    retryMeterStatus() {
        this.statusTries = { step0: 0, step1: 0, step2: 0 };
        this.checkMeterStatus();
        if (this.device === 'plug') {
            if (this.isEDGUser) {
                this.setStep(this.onboardingSteps.devices.plug.connectingOR);
            } else {
                this.setStep(this.onboardingSteps.devices.plug.connecting);
            }
        } else {
            this.setStep(this.onboardingSteps.devices.box.connecting);
        }
    }

    /**
     * Aktuellen Step der Registrierung setzen -> Im Frontend die Steps durchwechseln
     *
     * @param step
     */
    setStep(step: string) {
        this.currentStep = step;
        switch (this.currentStep) {
            case this.onboardingSteps.devices.box.powerConnect:
            case this.onboardingSteps.devices.box.lanConnect: {
                this.connect = { type: 'LAN', tries: 0 };
                break;
            }
            case this.onboardingSteps.devices.box.macEntry: {
                if (this.input.mac !== null && this.input.mac.length > 0) {
                    this.registerDevice(this.input.mac, false);
                }
                break;
            }
            case 'W':
            case 'X': {
                this.connect = { type: 'AP', tries: 0 };
                break;
            }
            case this.onboardingSteps.devices.plug.connecting:
            case this.onboardingSteps.devices.box.connecting:
                if (this.user.email !== null && this.user.email !== undefined) {
                    this.apiService.loginUser(this.user.email, this.user.password);
                }
                break;
            case this.onboardingSteps.accountSetup:
                break;
            case this.onboardingSteps.opticalReader.deviceSelection:
                this.startInitialRegistration();
                break;
        }

        // analytics stuff
        switch (this.currentStep) {
            case this.onboardingSteps.devices.box.lanConnect:
            case 'X': {
                this.tracking.onboarding_status(TRACKING_EVENT.ONBOARDING_START, {
                    connection_type: this.connect.type,
                    user_mac: this.user.mac,
                });
                break;
            }
            case 'I': {
                if (this.connect.type !== null) {
                    this.tracking.onboarding_status(TRACKING_EVENT.ONBOARDING_CANCEL, {
                        connection_type: this.connect.type,
                        user_mac: this.user.mac,
                    });
                }
                break;
            }
        }
    }

    /**
     * On Device selected
     * @param device
     * @param optical_reader
     * @param smart_reader
     * @param model
     */
    selectDevice(device, optical_reader = false, smart_reader = false, model = ''): void {
        this.device = device;
        let device_to_set = '';
        switch (device) {
            case 'box':
                this.deviceTitle = this.translate.instant('common.devices.boxEon');
                device_to_set = constants.application.devices.box;
                break;
            case 'plug':
                if (optical_reader) {
                    this.isEDGUser = true;
                    device_to_set = constants.application.devices.plug_optical;
                    this.deviceTitle = this.translate.instant('common.devices.plugEon');
                } else if (smart_reader) {
                    device_to_set = model;
                    this.isEDGUser = true;
                    this.deviceTitle = this.translate.instant('common.devices.box2Eon');
                    this.isBox2 = true;
                } else {
                    device_to_set = constants.application.devices.plug;
                    this.deviceTitle = this.translate.instant('common.devices.plugEon');
                }
                break;
            default:
                this.deviceTitle = this.translate.instant('common.devices.boxEon');
                device_to_set = constants.application.devices.box;
                break;
        }
        this.userService.updateUserDevice(device_to_set);
        this.setStep(this.onboardingSteps.hardwarePresent);
    }

    checkOptInResponse(
        trackingEnabled: boolean,
        optInEnabled: boolean,
        dataOptInEnabled: boolean
    ): void {
        this.tracking.changeTrackingState(trackingEnabled);
        if (!optInEnabled) {
            this.localOptInService.triggerOptInPopover(true).subscribe((finalResult) => {
                if (finalResult) {
                    // this.continueOnboardingAfterPermissionCheck();
                    this.sendDataAnalyticsOptin(dataOptInEnabled);
                } else {
                    this.apiService.logoutUser();
                }
            });
        } else {
            // this.continueOnboardingAfterPermissionCheck();
            this.sendDataAnalyticsOptin(dataOptInEnabled);
        }
    }

    afterConnectionFinished(): void {
        if (this.navigationFromDashboard) {
            this.router.navigate(['/']);
            return;
        }
        this.setStep(onboardingSteps.accountSetup);
    }

    setPIN(pin: string): void {
        this.readerPIN = parseInt(pin, 10);

        this.connectMeterWithPin();
        this.enterMeterStatusUpdate();
    }

    determineBackgroundColor(): string {
        return this.isCurrentStepColored() ? '#00A1B0' : '#e8e8e8';
    }

    isBright(): boolean {
        return !this.isCurrentStepColored();
    }

    setFirstMACAddress(value: string): void {
        this.firstMac = value.replace(/:/g, '');
        this.setStep(this.onboardingSteps.devices.box.macEntry2);
    }

    setPlugMacAddress(): void {
        const plainMac = this.deviceMacForm.value.mac.replace(/:/g, '');
        if (this.plugMacValidationRegex.test(plainMac)) {
            this.plugMac = plainMac;
            this.deviceMac = plainMac;
            this.setStep(this.onboardingSteps.devices.plug.orSetup.findAndEnterORMacAddress);
            this.initializeContinuousRegistrationCheck();
            return;
        }
        const errorText = this.translate.instant('common.errors.invalidMACAddressFormat');

        this.toast.error(errorText);
    }

    /**
     * Sets the MAC Adress of the optical reader
     */
    setOpticalReaderMacAddress(): void {
        const plainMac = this.readerMacForm.value.mac.replace(/:/g, '');
        if (this.opticalReaderMacValidationRegex.test(plainMac)) {
            this.opticalReaderMac = plainMac;
            this.setStep(this.onboardingSteps.devices.plug.orSetup.positioningOR);
            return;
        }
        this.toast.error('Die eingegebene MAC-Adresse entspricht nicht dem geforderten Format.');
    }

    /**
     * Starts the manual commissioning process where the connection between the plug and the
     * optical reader is established.
     */

    startCommission(): void {
        const maxRetries = 60;

        this.opticalReader
            .startCommission(this.opticalReaderMac)
            .pipe(
                retryWhen((errors) =>
                    errors.pipe(
                        concatMap((attempt) => {
                            if (attempt < maxRetries) {
                                console.log(`Retrying commissioning attempt ${attempt + 1}`);
                                this.commissionStatus = 'loading';

                                return timer(10000);
                            }

                            return throwError(new Error('Maximum retries exceeded'));
                        })
                    )
                )
            )
            .subscribe({
                next: () => {
                    this.commissionStatus = 'success';
                },
                error: () => {
                    this.commissionStatus = 'error';
                    this.openErrorDialog();
                    const errorText = this.translate.instant(
                        'common.errors.errorStartingOpticalReaderCommissioning'
                    );
                    this.toast.error(errorText);
                },
            });
    }

    startFirmwareUpdate(): void {
        this.startContinuousFirmwareUpdateSearch();
        this.setStep(onboardingSteps.devices.plug.orSetup.infoProcessDuration);
    }

    afterOrOnboardingComplete(): void {
        if (this.isEDGUser) {
            this.checkPinExistence();
        } else {
            this.registerDevice(this.plugMac, true);
        }
    }

    private initializeRouteParamterHandling(): void {
        this.route.queryParams.subscribe((params) => {
            const skipToOnboarding = params.jumpToOnboarding;
            const skipToMeterStatus = params.jumpToMeterstate;
            const skipToTutorial = params.jumpToTutorial;

            if (skipToOnboarding) {
                if (!this.userService.getActiveUserName()) {
                    this.router.navigate(['/registrieren']);
                    return;
                }
                this.registration.getModel().subscribe(
                    (model_response: any) => {
                        switch (model_response.model_identifier) {
                            case constants.application.devices.plug:
                                this.selectDevice('plug');
                                break;
                            case constants.application.devices.plug_optical:
                                this.isEDGUser = true;
                                this.selectDevice('plug', true);
                                break;
                            case constants.application.devices.smart_box:
                                this.isEDGUser = true;
                                this.selectDevice(
                                    'plug',
                                    false,
                                    true,
                                    constants.application.devices.smart_box
                                );
                                break;
                            case constants.application.devices.smart_box2:
                                this.isEDGUser = true;
                                this.selectDevice(
                                    'plug',
                                    false,
                                    true,
                                    constants.application.devices.smart_box2
                                );
                                break;
                            default:
                                this.selectDevice('box');
                        }
                    },
                    (error) => {
                        console.log('Error:', error);
                    }
                );
                return;
            }

            if (skipToMeterStatus) {
                if (!this.userService.getActiveUserName()) {
                    this.router.navigate(['/registrieren']);
                }
                this.registration.getModel().subscribe(
                    (res) => {
                        this.determineUserMode(res);
                        if (this.device === constants.application.devices.plug) {
                            if (this.isEDGUser) {
                                this.setStep(this.onboardingSteps.devices.plug.connectingOR);
                            } else {
                                this.setStep(this.onboardingSteps.devices.plug.connecting);
                            }
                        } else if (this.device === constants.application.devices.plug_optical) {
                            this.setStep(this.onboardingSteps.devices.plug.connectingOR);
                        } else {
                            this.setStep(this.onboardingSteps.devices.box.connecting);
                        }
                        this.navigationFromDashboard = true;
                        this.checkMeterStatus(true);
                    },
                    () => {
                        this.router.navigate(['/registrieren']);
                    }
                );
                return;
            }

            if (skipToTutorial) {
                this.registration.getModel().subscribe((modelResponse) => {
                    this.userService.setActiveUserProvider(modelResponse.labelpartner);
                    this.isEDGUser = this.userService.isEDGUser();
                    switch (modelResponse.model_identifier) {
                        case constants.application.devices.plug:
                            this.selectDevice('plug', true);
                            if (this.isEDGUser) {
                                this.setStep(this.onboardingSteps.devices.plug.macEntry);
                            }
                            break;
                        case constants.application.devices.plug_optical:
                        case constants.application.devices.smart_box:
                        case constants.application.devices.smart_box2:
                            this.isEDGUser = true;
                            this.selectDevice('plug', true);
                            this.setStep(this.onboardingSteps.devices.plug.macEntry);
                            break;
                        default:
                            this.selectDevice('box');
                            this.setStep(this.onboardingSteps.devices.box.powerConnect);
                    }
                });
                return;
            }
        });
    }

    private determineUserMode(data): void {
        this.device = data.model_identifier;
        switch (data.model_identifier) {
            case constants.application.devices.plug:
                this.deviceTitle = this.translate.instant('common.device.plug2');
                break;
            case constants.application.devices.plug_optical:
                this.deviceTitle = this.translate.instant('common.device.plug2');
                this.isEDGUser = true;
                break;
            case constants.application.devices.smart_box:
            case constants.application.devices.smart_box2:
                this.deviceTitle = this.translate.instant('common.device.box2Eon');
                this.isEDGUser = true;
                break;
            default:
                this.deviceTitle = this.translate.instant('common.device.boxEon');
        }
    }

    /**
     * Starts the continuous search for firmware updates
     * @private
     */
    private startContinuousFirmwareUpdateSearch(): void {
        const destroy$ = new Subject();
        const updateStarted = false;

        let currentCycle = 0;
        const totalCycles = this.firmwareUpdateSearchDuration / this.firmwareUpdateSearchInterval;
        const totalCyclesUpdating =
            this.firmwareUpdateMaxDuration / this.firmwareUpdateSearchInterval;
        timer(0, this.firmwareUpdateSearchInterval)
            .pipe(
                takeUntil(destroy$),
                mergeMap(() => this.opticalReader.getOpticalReaderStatus()),
                mergeMap((orStatus) => {
                    console.log('current or status', orStatus);
                    const currentStatus = orStatus.status;
                    const firmwareStatus = orStatus.firmware_status.toLowerCase();

                    let action: 'done' | 'startUpdate' | 'updating' | 'wait';

                    if (
                        this.lastMeterStatus === MeterStatuses.UPDATE_INSTALLING &&
                        currentStatus !== MeterStatuses.UPDATE_INSTALLING
                    ) {
                        action = 'done';
                        return of({ action, orStatus });
                    }

                    if (firmwareStatus === 'ok') {
                        action = 'done';
                    } else if (
                        firmwareStatus === 'update_installing' ||
                        currentStatus === MeterStatuses.UPDATE_INSTALLING
                    ) {
                        action = 'updating';
                    } else {
                        if (
                            !firmwareStatus ||
                            firmwareStatus === '' ||
                            firmwareStatus === 'update_available'
                        ) {
                            action = 'wait';
                        } else {
                            console.log('undefined state');
                            action = 'wait';
                        }
                    }
                    this.lastMeterStatus = currentStatus;
                    return of({ action, orStatus });
                }),
                switchMap(({ action, orStatus }) => {
                    console.log('current action', action);
                    switch (action) {
                        case 'done':
                            this.updateService.onUpdateStateReceived.next(null);
                            return of(true);
                        case 'updating':
                            if ('update_progress' in orStatus) {
                                this.updateService.onUpdateStateReceived.next(
                                    orStatus.update_progress
                                );
                            }
                            if (!this.popoverOpen) {
                                this.popoverOpen = true;
                                const config =
                                    this.popoverConfigService.getFirmwareUpdatePopoverConfig();
                                return this.popover.open(config).afterClosed$.pipe(
                                    map(() => {
                                        this.popoverOpen = false;
                                        return true;
                                    })
                                );
                            }
                            return of(false);
                        case 'wait':
                            this.firmwareUpdateStatus = 'loading';
                            return of(false);
                    }
                })
            )
            .subscribe({
                next: (result) => {
                    if (result) {
                        destroy$.next(null);
                        this.firmwareUpdateFinishedOrFailed = true;
                        this.firmwareUpdateStatus = 'uptodate';
                    }

                    ++currentCycle;
                    if (updateStarted) {
                        if (currentCycle === totalCyclesUpdating) {
                            destroy$.next(null);
                            this.firmwareUpdateStatus = 'error';
                        }
                    } else {
                        if (currentCycle === totalCycles) {
                            destroy$.next(null);
                            this.firmwareUpdateStatus = 'error';
                        }
                    }
                },
            });
    }

    /**
     * Starts a timer that checks the meter status every 5 seconds
     * @private
     */
    private initializeContinuousRegistrationCheck(): void {
        const destroy$ = new Subject();
        const totalNumberOfTries =
            this.registrationOnboardRetryDuration / this.registrationOnboardRetryInterval;
        let currentNumberOfTries = 0;
        timer(0, 5000)
            .pipe(
                takeUntil(destroy$),
                switchMap(() =>
                    this.registration.registerDevice(this.plugMac).pipe(
                        mergeMap((response) => {
                            throw { message: 'Error during registration', error: response };
                        }),
                        catchError((error) => {
                            try {
                                if (error?.error?.error?.code === 102) {
                                    return of(true);
                                }
                            } catch (error) {
                                return of(false);
                            }
                            return of(false);
                        })
                    )
                )
            )
            .subscribe({
                next: (result) => {
                    ++currentNumberOfTries;
                    if (currentNumberOfTries === totalNumberOfTries) {
                        destroy$.next(null);
                    }
                    if (result) {
                        destroy$.next(null);
                        this.registrationOnboardSuccessful = true;
                    }
                },
            });
    }

    private shouldTriggerTimeBasedOverlay(item: string, timeframe: any, time: number): boolean {
        const lastTriggered = localStorage.getItem(item);
        if (!lastTriggered) {
            localStorage.setItem(item, moment().toDate().toString());
            return true;
        }

        const date = new Date(lastTriggered);
        if (date <= moment().subtract(time, timeframe).toDate()) {
            localStorage.setItem(item, moment().toDate().toString());
            return true;
        }
        return false;
    }

    private shouldTriggerAvailableUpdateOverlay(): boolean {
        const storageItem = 'lastUpdateInfo';
        const lastTriggered = localStorage.getItem(storageItem);
        if (!lastTriggered) {
            localStorage.setItem(storageItem, moment().toDate().toString());
            return true;
        }

        const date = new Date(lastTriggered);
        if (date <= moment().subtract(24, 'hour').toDate()) {
            localStorage.setItem(storageItem, moment().toDate().toString());
            return true;
        }
        return false;
    }

    private showUpdateAvailableOverlay(): void {
        if (!this.shouldTriggerAvailableUpdateOverlay()) {
            return;
        }
        if (this.popoverOpen) {
            return;
        }
        const config = this.popoverConfigService.getFirmwareUpdateAvailablePopoverConfig();
        this.popover
            .open(config)
            .afterClosed$.pipe(
                mergeMap((res) => of({ type: MeterStatuses.CONNECTED_WITH_METER, res }))
            );
        this.popoverOpen = true;
    }

    private showManualPinEntryOverlay(): void {
        if (!this.popoverOpen) {
            const overlayDef = this.popoverConfigService.getManualPinEntryInfoPopoverConfig();
            if (this.meterPin) {
                overlayDef.data.text += ` Ihre aktuelle Zähler-Pin lautet: ${this.meterPin}.`;
            }
            this.popover.open(overlayDef).afterClosed$.subscribe(() => {
                this.popoverOpen = false;
                this.preventPinInfoPopover = true;
            });
            this.popoverOpen = true;
        }
    }

    /**
     * Handle the PIN_FAILED Meter State
     */
    private handlePinFailedState(): void {
        const storageItem = 'lastPinInfo';

        let triggerOverlay = false;
        const lastTriggered = localStorage.getItem(storageItem);
        if (!lastTriggered) {
            localStorage.setItem(storageItem, moment().toDate().toString());
            triggerOverlay = true;
        }

        const date = new Date(lastTriggered);
        if (date <= moment().subtract(1, 'hour').toDate()) {
            localStorage.setItem(storageItem, moment().toDate().toString());
            triggerOverlay = true;
        }

        if (!triggerOverlay) {
            return;
        }

        const config = this.popoverConfigService.getPinFailedPopoverConfig();
        this.popover
            .open(config)
            .afterClosed$.pipe(
                mergeMap((dialogData) => {
                    if (dialogData.data === null) {
                        this.popoverOpen = false;
                        return null;
                    }
                    let overlayDef: any =
                        this.popoverConfigService.getManualPinEntryPopoverConfig();
                    if (dialogData.data) {
                        overlayDef = this.popoverConfigService.getPinEntryPopoverConfig();
                    }
                    if (this.meterPin) {
                        const updatedText = ` ${this.translate.instant('screens.settings.currentPin', { pincode: this.meterPin })}`;
                        overlayDef.data.text += updatedText;
                    }
                    return this.popover.open(overlayDef).afterClosed$;
                })
            )
            .subscribe((result: any) => {
                if (result === null) {
                    this.popoverOpen = false;
                    return;
                }
                if (typeof result.data === 'string') {
                    const pin = parseInt(result.data, 10);
                    this.meter.startContinuousPinEntry(pin);
                }
                if (result) {
                    this.popoverOpen = false;
                }

                this.popoverOpen = false;
            });

        this.popoverOpen = true;
    }

    private isCurrentStepColored(): boolean {
        const colored_steps = [
            this.onboardingSteps.deviceSelection,
            this.onboardingSteps.devices.box.powerConnect,
            this.onboardingSteps.devices.box.lanConnect,
            this.onboardingSteps.devices.box.connecting,
            this.onboardingSteps.devices.plug.noteMac,
            this.onboardingSteps.devices.plug.powerConnect,
            this.onboardingSteps.devices.plug.wpsConnect,
            this.onboardingSteps.devices.plug.wpsRouter,
            this.onboardingSteps.devices.plug.wifiConnected,
            this.onboardingSteps.devices.plug.connecting,
            this.onboardingSteps.devices.plug.orSetup.findAndEnterORMacAddress,
            this.onboardingSteps.devices.plug.orSetup.positioningOR,
            this.onboardingSteps.devices.plug.orSetup.preparingPlug,
            this.onboardingSteps.devices.plug.orSetup.powerReaderAndStartCommission,
            this.onboardingSteps.devices.plug.orSetup.infoProcessDuration,
            this.onboardingSteps.devices.plug.orSetup.readerInstallationStep1,
            this.onboardingSteps.devices.plug.orSetup.readerInstallationStep2,
            this.onboardingSteps.devices.plug.orSetup.readerInstallationStep3,
            this.onboardingSteps.devices.plug.orSetup.readerInstallationStep4,
            this.onboardingSteps.opticalReader.deviceSelection,
            this.onboardingSteps.opticalReader.installation.step1,
            this.onboardingSteps.opticalReader.installation.step2,
            this.onboardingSteps.opticalReader.installation.step3,
            this.onboardingSteps.opticalReader.installation.step4,
            this.onboardingSteps.devices.plug.connectingOR,
            onboardingSteps.accountSetup,
        ];
        return colored_steps.findIndex((val) => this.currentStep === val) >= 0;
    }

    private openErrorPopover(data: any): void {
        if (this.popoverOpen) {
            return;
        }

        let config = null;
        let doReallyShow = true;
        switch (data.current_status.toUpperCase()) {
            case 'CONTACT_WRONG_METER_SERIAL': {
                config = this.popoverConfigService.getOnboardingWrongSerialPopoverConfig();
                const read = data.smartreader.meter_id_read;
                const provisioned = data.smartreader.meter_id_provisioned;
                doReallyShow = read !== provisioned;
                config.data.text = this.translate.instant(
                    'screens.register.onBoarding.errorMessages.wrongMeterSerial',
                    {
                        read,
                        provisioned,
                    }
                );
                break;
            }
            case 'CONTACT_NO_DATA_FROM_METER': {
                if (this.noDataPopoverShown) {
                    return;
                }
                config = this.popoverConfigService.getOnboardingNoContactPopoverConfig();
                break;
            }
            default:
                config = this.popoverConfigService.getOnboardingNoContactPopoverConfig();

                break;
        }

        if (!doReallyShow) {
            return;
        }

        this.popover.open(config).afterClosed$.subscribe(() => {
            this.connectMeterWithPin();
            this.popoverOpen = false;

            if (data.current_status.toUpperCase() === 'CONTACT_NO_DATA_FROM_METER') {
                this.noDataPopoverShown = true;
            }
        });

        this.popoverOpen = true;
    }

    private startInitialRegistration(): void {
        if (new Date().getTime() - this.lastRegistrationCall <= 5000) {
            this.startInitialRegistration();
            return;
        }
        const mac = this.secondMac.replace(/:/g, '');
        const macPart1 = mac.substring(0, 6);
        const macPart2 = mac.substring(6);
        const finalMac = macPart1 + '0000' + macPart2;
        this.registrationSub = this.registration
            .registerDevice(finalMac)
            .pipe(
                mergeMap((res) => (res ? of(true) : throwError(undefined))),
                catchError((error) => {
                    let isSuccessError = false;
                    try {
                        isSuccessError = error.error.code === 102;
                    } catch (e) {
                        console.log('Error:', e);
                        return throwError(undefined);
                    }
                    return isSuccessError ? of(true) : throwError(undefined);
                })
            )
            .subscribe(
                () => {
                    this.registrationSub.unsubscribe();
                    this.registrationSub = null;
                },
                () => {
                    if (this.registrationTries < 60) {
                        this.startInitialRegistration();
                    } else {
                        this.registrationSub.unsubscribe();
                        this.registrationSub = null;
                    }
                    this.lastRegistrationCall = new Date().getTime();
                }
            );
    }

    private sendDataAnalyticsOptin(value: boolean): void {
        this.optInService.setAnalyticsOptIn(value).subscribe({
            next: () => {
                this.continueOnboardingAfterPermissionCheck();
            },
        });
    }

    private continueOnboardingAfterPermissionCheck(): void {
        switch (this.device) {
            case 'plug':
                if (this.isEDGUser) {
                    this.currentStep = this.onboardingSteps.devices.plug.noteMac;
                    break;
                }
                this.currentStep = this.onboardingSteps.devices.plug.noteMac;
                break;
            default:
                this.setStep(this.onboardingSteps.devices.box.powerConnect);
                break;
        }
    }

    private selectConnectionStep(device: string): void {
        if (device === 'plug') {
            if (this.isEDGUser) {
                this.setStep(this.onboardingSteps.devices.plug.connectingOR);
            } else {
                this.setStep(this.onboardingSteps.devices.plug.connecting);
            }
        } else {
            this.setStep(this.onboardingSteps.devices.box.connecting);
        }
    }

    private setupFormHandling(): void {
        this.passwordForm.valueChanges
            .pipe(
                tap((values) => (this.currentPasswordScore = zxcvbn(values.password1).score)),
                map((value) => {
                    const first = this.passwordRegex.test(value.password1);
                    const second = this.passwordRegex.test(value.password2);
                    return first && second;
                })
            )
            .subscribe((result) => {
                this.setPasswordDisabled = !result;
            });
    }

    private checkPinExistence(): void {
        this.opticalReader.getOpticalReaderStatus(true).subscribe({
            next: (orStatus) => {
                try {
                    const pincode = orStatus.electricity.smartreader.pincode;
                    if (pincode || pincode === '') {
                        this.setPIN(pincode);
                    } else {
                        this.setStep(this.onboardingSteps.opticalReader.pinEntry);
                    }
                } catch (error) {
                    this.setStep(this.onboardingSteps.opticalReader.pinEntry);
                }
            },
        });
    }

    openVideoFullscreen(videoId: string) {
        this.dialog.open(VideoDialogComponent, {
            width: '100vw',
            height: '100vh',
            maxWidth: '100vw',
            data: {
                videoId,
            },
        });
    }
}
